/**
 * BoardColumn Component
 *
 * Represents a single driver's column with assigned jobs
 *
 * Features:
 * - Droppable zone for job cards
 * - Shows driver name in header
 * - Remove driver action
 * - Sortable job list
 * - Empty state
 */

import { useDroppable } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Trash2, User } from 'lucide-react';
import { JobCard } from './JobCard';
import type { JobAssignment } from '../types/dispatch.types';

interface BoardColumnProps {
  driverName: string;
  assignments: JobAssignment[];
  onRemoveDriver?: () => void;
  onUnassignJob: (jobId: string) => void;
  onJobClick?: (assignment: JobAssignment) => void;
  isUnassignedColumn?: boolean;
}

export function BoardColumn({
  driverName,
  assignments,
  onRemoveDriver,
  onUnassignJob,
  onJobClick,
  isUnassignedColumn = false,
}: BoardColumnProps) {
  const { setNodeRef, isOver } = useDroppable({
    id: driverName,
  });

  const assignmentIds = assignments.map((a) => a.id);

  return (
    <Card
      className={`w-80 flex-shrink-0 ${isOver ? 'ring-2 ring-blue-500' : ''}`}
      ref={setNodeRef}
    >
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <User className="h-5 w-5 text-gray-600" />
            <h3 className="font-semibold text-lg truncate" title={driverName}>
              {driverName}
            </h3>
            <Badge variant="secondary" className="ml-2">
              {assignments.length}
            </Badge>
          </div>
          {!isUnassignedColumn && onRemoveDriver && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onRemoveDriver}
              className="h-8 w-8 p-0 hover:bg-red-100"
              aria-label={`Remove ${driverName}`}
            >
              <Trash2 className="h-4 w-4 text-red-600" />
            </Button>
          )}
        </div>
      </CardHeader>

      <CardContent className="p-0">
        <ScrollArea className="h-[calc(100vh-250px)]">
          <div className="p-4 space-y-3">
            {assignments.length === 0 ? (
              <div className="text-center py-8 text-gray-400">
                <p className="text-sm">
                  {isUnassignedColumn
                    ? 'No unassigned jobs'
                    : 'Drop jobs here to assign'}
                </p>
              </div>
            ) : (
              <SortableContext items={assignmentIds} strategy={verticalListSortingStrategy}>
                {assignments.map((assignment) => (
                  <JobCard
                    key={assignment.id}
                    assignment={assignment}
                    onUnassign={() => onUnassignJob(assignment.jobId)}
                    onClick={() => onJobClick?.(assignment)}
                  />
                ))}
              </SortableContext>
            )}
          </div>
        </ScrollArea>
      </CardContent>
    </Card>
  );
}
